import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import {
  FileText,
  FilePlus,
  Clock,
  Eye,
  TrendingUp,
  Calendar,
  Star,
  AlertCircle,
} from "lucide-react";
import { formatarData } from "@/hooks/useBlog";

interface DashboardStats {
  totalArtigos: number;
  publicados: number;
  rascunhos: number;
  agendados: number;
  emDestaque: number;
  pendentesAprovacao: number;
}

interface ArtigoRecente {
  id: string;
  titulo: string;
  slug: string;
  publicado: boolean;
  destaque: boolean;
  data_publicacao: string;
  status_aprovacao: string | null;
  categoria?: { nome: string } | null;
}

export default function AdminDashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async (): Promise<DashboardStats> => {
      const now = new Date().toISOString();

      const [totalRes, publicadosRes, rascunhosRes, agendadosRes, destaqueRes, pendentesRes] =
        await Promise.all([
          supabase.from("artigos").select("id", { count: "exact", head: true }),
          supabase
            .from("artigos")
            .select("id", { count: "exact", head: true })
            .eq("publicado", true)
            .lte("data_publicacao", now),
          supabase
            .from("artigos")
            .select("id", { count: "exact", head: true })
            .eq("publicado", false),
          supabase
            .from("artigos")
            .select("id", { count: "exact", head: true })
            .eq("publicado", true)
            .gt("data_publicacao", now),
          supabase
            .from("artigos")
            .select("id", { count: "exact", head: true })
            .eq("destaque", true),
          supabase
            .from("artigos")
            .select("id", { count: "exact", head: true })
            .eq("status_aprovacao", "aguardando_aprovacao"),
        ]);

      return {
        totalArtigos: totalRes.count || 0,
        publicados: publicadosRes.count || 0,
        rascunhos: rascunhosRes.count || 0,
        agendados: agendadosRes.count || 0,
        emDestaque: destaqueRes.count || 0,
        pendentesAprovacao: pendentesRes.count || 0,
      };
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: artigosRecentes, isLoading: artigosLoading } = useQuery({
    queryKey: ["admin-artigos-recentes"],
    queryFn: async (): Promise<ArtigoRecente[]> => {
      const { data, error } = await supabase
        .from("artigos")
        .select(`
          id,
          titulo,
          slug,
          publicado,
          destaque,
          data_publicacao,
          status_aprovacao,
          categoria:categorias_blog(nome)
        `)
        .order("updated_at", { ascending: false })
        .limit(5);

      if (error) throw error;
      return data as ArtigoRecente[];
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: artigosPendentes } = useQuery({
    queryKey: ["admin-pendentes"],
    queryFn: async (): Promise<ArtigoRecente[]> => {
      const { data, error } = await supabase
        .from("artigos")
        .select(`
          id,
          titulo,
          slug,
          publicado,
          destaque,
          data_publicacao,
          status_aprovacao,
          categoria:categorias_blog(nome)
        `)
        .eq("status_aprovacao", "aguardando_aprovacao")
        .order("updated_at", { ascending: false })
        .limit(5);

      if (error) throw error;
      return data as ArtigoRecente[];
    },
    staleTime: 1000 * 60 * 2,
  });

  const statCards = [
    {
      title: "Pendentes",
      value: stats?.pendentesAprovacao || 0,
      icon: AlertCircle,
      color: "text-amber-600",
      bgColor: "bg-amber-100",
    },
    {
      title: "Total de Artigos",
      value: stats?.totalArtigos || 0,
      icon: FileText,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      title: "Publicados",
      value: stats?.publicados || 0,
      icon: Eye,
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      title: "Rascunhos",
      value: stats?.rascunhos || 0,
      icon: Clock,
      color: "text-yellow-600",
      bgColor: "bg-yellow-100",
    },
    {
      title: "Agendados",
      value: stats?.agendados || 0,
      icon: Calendar,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Dashboard | Aceena Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground">Visão geral do blog Aceena</p>
          </div>
          <Button asChild>
            <Link to="/admin/artigos/novo">
              <FilePlus className="h-4 w-4 mr-2" />
              Novo Artigo
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {statCards.map((stat) => (
            <Card key={stat.title}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <stat.icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{stat.title}</p>
                    {statsLoading ? (
                      <Skeleton className="h-6 w-12 mt-1" />
                    ) : (
                      <p className="text-xl font-bold text-foreground">{stat.value}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {artigosPendentes && artigosPendentes.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <AlertCircle className="h-5 w-5" />
                Pendentes de Aprovação ({artigosPendentes.length})
              </CardTitle>
              <CardDescription>Artigos aguardando revisão</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {artigosPendentes.map((artigo) => (
                  <Link
                    key={artigo.id}
                    to={`/admin/artigos/${artigo.id}/preview`}
                    className="flex items-center gap-3 p-4 bg-white rounded-lg border border-amber-200 hover:border-amber-400 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{artigo.titulo}</p>
                      <p className="text-sm text-muted-foreground">
                        {artigo.categoria?.nome || "Sem categoria"}
                      </p>
                    </div>
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800 flex-shrink-0">
                      Revisar
                    </Badge>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Artigos Recentes
              </CardTitle>
              <CardDescription>Últimos artigos atualizados</CardDescription>
            </CardHeader>
            <CardContent>
              {artigosLoading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : artigosRecentes && artigosRecentes.length > 0 ? (
                <div className="space-y-3">
                  {artigosRecentes.map((artigo) => {
                    const isAgendado = artigo.publicado && new Date(artigo.data_publicacao) > new Date();
                    const isAguardando = artigo.status_aprovacao === "aguardando_aprovacao";

                    return (
                      <Link
                        key={artigo.id}
                        to={isAguardando ? `/admin/artigos/${artigo.id}/preview` : `/admin/artigos/${artigo.id}`}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-foreground truncate">{artigo.titulo}</p>
                            {artigo.destaque && (
                              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            {artigo.categoria && <span>{artigo.categoria.nome}</span>}
                            <span>•</span>
                            <span>{formatarData(artigo.data_publicacao)}</span>
                          </div>
                        </div>
                        <Badge
                          variant={isAguardando ? "secondary" : isAgendado ? "secondary" : artigo.publicado ? "default" : "outline"}
                          className={isAguardando ? "bg-amber-100 text-amber-800" : ""}
                        >
                          {isAguardando ? "Aguardando" : isAgendado ? "Agendado" : artigo.publicado ? "Publicado" : "Rascunho"}
                        </Badge>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">Nenhum artigo encontrado</p>
              )}
              <div className="mt-4 pt-4 border-t border-border">
                <Button variant="outline" asChild className="w-full">
                  <Link to="/admin/artigos">Ver todos os artigos</Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Ações Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button asChild variant="outline" className="w-full justify-start">
                <Link to="/admin/artigos/novo">
                  <FilePlus className="h-4 w-4 mr-2" />
                  Criar novo artigo
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-start">
                <Link to="/admin/artigos">
                  <FileText className="h-4 w-4 mr-2" />
                  Gerenciar artigos
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-start">
                <Link to="/admin/categorias">
                  <FileText className="h-4 w-4 mr-2" />
                  Gerenciar categorias
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-start">
                <a href="/blog" target="_blank" rel="noopener noreferrer">
                  <Eye className="h-4 w-4 mr-2" />
                  Ver blog público
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
